import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";

import productsHero from "@/assets/hero/products.png";
import { useHeroSlides } from "@/hooks/useSiteContent";

const DEFAULT_ALT = "Curated selection of perfumes, groceries, beverages, and chocolates";

export const Hero = () => {
  const { data: heroSlides } = useHeroSlides();
  const heroImage = heroSlides?.[0]?.url ?? productsHero;
  const heroAlt = heroSlides?.[0]?.alt ?? DEFAULT_ALT;

  return (
    <section
      id="hero"
      className="relative flex flex-col h-[100dvh] box-border pt-24 md:pt-28"
      data-testid="hero-section"
    >
      <div className="flex-shrink-0 max-w-[1600px] mx-auto px-6 lg:px-12 w-full text-center py-3 md:py-4 lg:py-5">
        <motion.h1
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="font-heading font-black tracking-tighter text-4xl sm:text-5xl md:text-5xl lg:text-6xl xl:text-7xl uppercase text-vg-navy leading-[0.95]"
          data-testid="hero-headline"
        >
          Everyday{" "} 
          <span className="text-vg-cyan">
            Essentials <span className="text-vg-navy">&amp;</span> Global Flavors
          </span> Sourced Worldwide.
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="mt-3 md:mt-4 mx-auto text-sm md:text-base lg:text-lg text-vg-gray leading-relaxed lg:whitespace-nowrap max-w-3xl"
        >
          Connecting you with premium foods and beverages from trusted global retail partners.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="mt-4 md:mt-5 flex flex-wrap items-center justify-center gap-3"
        >
          <a
            href="#categories"
            onClick={(e) => {
              e.preventDefault();
              document.querySelector("#categories")?.scrollIntoView({ behavior: "smooth" });
            }}
            className="inline-flex items-center gap-2 bg-vg-navy text-white px-6 md:px-8 py-3 md:py-3.5 text-sm md:text-base font-medium hover:bg-vg-cyan transition-colors"
            data-testid="hero-explore-button"
          >
            Discover our products
            <ArrowRight size={18} />
          </a>
          <a
            href="#contact"
            onClick={(e) => {
              e.preventDefault();
              document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" });
            }}
            className="inline-flex items-center bg-[#f3f4f6] text-vg-ink px-6 md:px-8 py-3 md:py-3.5 text-sm md:text-base font-medium hover:bg-[#e5e7eb] transition-colors"
            data-testid="hero-quote-button"
          >
            Request a quote
          </a>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 32 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.45, duration: 1, ease: [0.16, 1, 0.3, 1] }}
        className="flex-1 min-h-[220px] md:min-h-[42vh] lg:min-h-[52vh] w-full px-4 sm:px-6 lg:px-12 pb-4 sm:pb-6"
      >
        <div className="h-full w-full border border-black/10 overflow-hidden">
          <img
            src={heroImage}
            alt={heroAlt}
            className="w-full h-full object-cover object-center block"
            data-testid="hero-image"
          />
        </div>
      </motion.div>
    </section>
  );
};

export default Hero;
